import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    BimCreateContractConditionOtherCostMutationVariables,
    BimCreateWorkOrderOtherFromCatalogForActMutationVariables,
    BimCreateWorkOrderOtherFromInputForActMutationVariables,
    ChapterEnum,
    MutationUerCreateContractOtherCostArgs,
    MutationUerCreateContractOtherCostFromCatalogArgs,
} from 'gql/graphql';
import { OtherCostCalculationTypeEnum, ProjectMethodologyEnum, SectionEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { replacerCost } from 'utils/replacerCost';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import type { TypeItem } from '../commonData';
import { CreatingTypes, OTHER_SECTION_OR_CHAPTER } from '../commonData';
import {
    bimCreateContractConditionOtherCostGql,
    bimCreateOtherToWorkOrderFromCatalogGgl,
    bimCreateOtherToWorkOrderFromInputGql,
    bimCreateWorkOrderChangeOtherFromCatalogGql,
    bimCreateWorkOrderChangeOtherFromInputGql,
    bimCreateWorkOrderOtherFromCatalogForActGql,
    bimCreateWorkOrderOtherFromInputForActGql,
    uerCreateContractOtherCostFromCatalogGql,
    uerCreateContractOtherCostGql,
} from '../createOtherQueries';

interface IUseContractOtherCreate {
    contractId: number;
    methodology: ProjectMethodologyEnum;
    /* Способ создания прочих. 'Лимиты по прочим затратам' => Вручную */
    creatingType: CreatingTypes;
    selectedType: TypeItem;
    selectedChapterOrSection: { id: ChapterEnum; label: string } | { id: SectionEnum; label: string };
    /* Запускается из 'Лимиты по прочим затратам' (Создание прочих в договоре) */
    isCondition?: boolean;
    workOrderId?: number;
    workOrderChangeId?: number;
    workOrderObjectId?: number;
    workOrderStatus?: WorkOrderStatusEnum;
}

const requestId = 'createOther';

export const useContractOtherCreate = (props: IUseContractOtherCreate) => {
    const dispatch = useDispatch();
    const {
        contractId,
        methodology,
        creatingType,
        isCondition,
        selectedChapterOrSection,
        selectedType,
        workOrderId,
        workOrderObjectId,
        workOrderStatus,
        workOrderChangeId,
    } = props;

    const isUer = useMemo(() => methodology === ProjectMethodologyEnum.Uer, [methodology]);
    const isManual = useMemo(() => creatingType === CreatingTypes.MANUAL, [creatingType]);
    const isBimWorkOrderChange = useMemo(() => Boolean(workOrderChangeId), [workOrderChangeId]);

    const [createOtherManuallyData, createOtherManuallyStatus, , createOtherManuallyRequest] = useRequest({
        requestId,
        query: uerCreateContractOtherCostGql,
        withEffect: false,
    });

    const [
        createContractOtherFromCatalogData,
        createContractOtherFromCatalogStatus,
        ,
        createContractOtherFromCatalogRequest,
    ] = useRequest({
        requestId,
        query: uerCreateContractOtherCostFromCatalogGql,
        withEffect: false,
    });

    const [
        bimCreateContractConditionOtherCostData,
        bimCreateContractConditionOtherCostStatus,
        ,
        bimCreateContractConditionOtherCostRequest,
    ] = useRequest({
        requestId,
        query: bimCreateContractConditionOtherCostGql,
        withEffect: false,
    });

    const [
        bimCreateOtherToWorkOrderFromCatalogData,
        bimCreateOtherToWorkOrderFromCatalogStatus,
        ,
        bimCreateOtherToWorkOrderFromCatalogRequest,
    ] = useRequest({
        requestId,
        query: bimCreateOtherToWorkOrderFromCatalogGgl,
        withEffect: false,
    });

    const [
        bimCreateWorkOrderOtherFromCatalogForActData,
        bimCreateWorkOrderOtherFromCatalogForActStatus,
        ,
        bimCreateWorkOrderOtherFromCatalogForActRequest,
    ] = useRequest({
        requestId,
        query: bimCreateWorkOrderOtherFromCatalogForActGql,
        withEffect: false,
    });

    const [
        bimCreateOtherToWorkOrderFromInputData,
        bimCreateOtherToWorkOrderFromInputStatus,
        ,
        bimCreateOtherToWorkOrderFromInputRequest,
    ] = useRequest({
        requestId,
        query: bimCreateOtherToWorkOrderFromInputGql,
        withEffect: false,
    });

    const [
        bimCreateWorkOrderOtherFromInputForActData,
        bimCreateWorkOrderOtherFromInputForActStatus,
        ,
        bimCreateWorkOrderOtherFromInputForActRequest,
    ] = useRequest({
        requestId,
        query: bimCreateWorkOrderOtherFromInputForActGql,
        withEffect: false,
    });

    const [
        bimCreateWorkOrderChangeOtherFromCatalogData,
        bimCreateWorkOrderChangeOtherFromCatalogStatus,
        ,
        bimCreateWorkOrderChangeOtherFromCatalogRequest,
    ] = useRequest({
        requestId,
        query: bimCreateWorkOrderChangeOtherFromCatalogGql,
        withEffect: false,
    });
    const [
        bimCreateWorkOrderChangeOtherFromInputData,
        bimCreateWorkOrderChangeOtherFromInputStatus,
        ,
        bimCreateWorkOrderChangeOtherFromInputRequest,
    ] = useRequest({
        requestId,
        query: bimCreateWorkOrderChangeOtherFromInputGql,
        withEffect: false,
    });

    const data = useMemo(() => {
        if (isManual) {
            if (isUer) {
                return createOtherManuallyData?.uerCreateContractOtherCost?.data;
            }

            if (isCondition) {
                return bimCreateContractConditionOtherCostData?.bimCreateContractConditionOtherCost?.data;
            }

            if (isBimWorkOrderChange) {
                return bimCreateWorkOrderChangeOtherFromInputData?.bimCreateWorkOrderChangeOtherFromInput;
            }

            if (workOrderStatus === WorkOrderStatusEnum.OnFormation) {
                return bimCreateOtherToWorkOrderFromInputData?.bimCreateWorkOrderOtherFromInput;
            }

            if (workOrderStatus === WorkOrderStatusEnum.InProgress) {
                return bimCreateWorkOrderOtherFromInputForActData?.bimCreateWorkOrderOtherFromInputForAct;
            }
        }

        if (isUer) {
            return createContractOtherFromCatalogData?.uerCreateContractOtherCostFromCatalog?.data;
        }

        if (isBimWorkOrderChange) {
            return bimCreateWorkOrderChangeOtherFromCatalogData?.bimCreateWorkOrderChangeOtherFromCatalog;
        }

        if (workOrderStatus === WorkOrderStatusEnum.OnFormation) {
            return bimCreateOtherToWorkOrderFromCatalogData?.bimCreateWorkOrderOtherFromCatalog;
        }

        if (workOrderStatus === WorkOrderStatusEnum.InProgress) {
            return bimCreateWorkOrderOtherFromCatalogForActData?.bimCreateWorkOrderOtherFromCatalogForAct;
        }

        return undefined;
    }, [
        bimCreateContractConditionOtherCostData?.bimCreateContractConditionOtherCost?.data,
        bimCreateOtherToWorkOrderFromCatalogData?.bimCreateWorkOrderOtherFromCatalog,
        bimCreateOtherToWorkOrderFromInputData?.bimCreateWorkOrderOtherFromInput,
        bimCreateWorkOrderChangeOtherFromCatalogData?.bimCreateWorkOrderChangeOtherFromCatalog,
        bimCreateWorkOrderChangeOtherFromInputData?.bimCreateWorkOrderChangeOtherFromInput,
        bimCreateWorkOrderOtherFromCatalogForActData?.bimCreateWorkOrderOtherFromCatalogForAct,
        bimCreateWorkOrderOtherFromInputForActData?.bimCreateWorkOrderOtherFromInputForAct,
        createContractOtherFromCatalogData?.uerCreateContractOtherCostFromCatalog?.data,
        createOtherManuallyData?.uerCreateContractOtherCost?.data,
        isBimWorkOrderChange,
        isCondition,
        isManual,
        isUer,
        workOrderStatus,
    ]);

    const status = useMemo(() => {
        if (isManual) {
            if (isUer) {
                return createOtherManuallyStatus;
            }

            if (isCondition) {
                return bimCreateContractConditionOtherCostStatus;
            }

            if (isBimWorkOrderChange) {
                return bimCreateWorkOrderChangeOtherFromInputStatus;
            }

            if (workOrderStatus === WorkOrderStatusEnum.OnFormation) {
                return bimCreateOtherToWorkOrderFromInputStatus;
            }

            if (workOrderStatus === WorkOrderStatusEnum.InProgress) {
                return bimCreateWorkOrderOtherFromInputForActStatus;
            }
        }

        if (isUer) {
            return createContractOtherFromCatalogStatus;
        }

        if (isBimWorkOrderChange) {
            return bimCreateWorkOrderChangeOtherFromCatalogStatus;
        }

        if (workOrderStatus === WorkOrderStatusEnum.OnFormation) {
            return bimCreateOtherToWorkOrderFromCatalogStatus;
        }

        if (workOrderStatus === WorkOrderStatusEnum.InProgress) {
            return bimCreateWorkOrderOtherFromCatalogForActStatus;
        }

        return undefined;
    }, [
        bimCreateContractConditionOtherCostStatus,
        bimCreateOtherToWorkOrderFromCatalogStatus,
        bimCreateOtherToWorkOrderFromInputStatus,
        bimCreateWorkOrderChangeOtherFromCatalogStatus,
        bimCreateWorkOrderChangeOtherFromInputStatus,
        bimCreateWorkOrderOtherFromCatalogForActStatus,
        bimCreateWorkOrderOtherFromInputForActStatus,
        createContractOtherFromCatalogStatus,
        createOtherManuallyStatus,
        isBimWorkOrderChange,
        isCondition,
        isManual,
        isUer,
        workOrderStatus,
    ]);

    const [reset] = useResetStoreData({ requestId });

    const makeRequest = useCallback(
        (formData: any, limitsArray: any[], formula: string) => {
            const calculatingTypeValue =
                selectedType.id === OtherCostCalculationTypeEnum.Percent
                    ? {
                          calculatingTypeValue: replacerCost(formData?.percent),
                      }
                    : {};

            const chapter: ChapterEnum | null =
                selectedChapterOrSection === OTHER_SECTION_OR_CHAPTER[0]
                    ? (selectedChapterOrSection.id as ChapterEnum)
                    : null;
            const section: SectionEnum =
                selectedChapterOrSection === OTHER_SECTION_OR_CHAPTER[1]
                    ? (selectedChapterOrSection.id as SectionEnum)
                    : SectionEnum.Others;
            const otherCostType = formData?.otherCostType
                ? {
                      catalogOtherCostTypeId: formData?.otherCostType?.id,
                  }
                : {};
            const otherCode = formData?.key
                ? {
                      key: formData?.key,
                  }
                : {};

            const otherCostLimits = limitsArray.map((l) => ({
                constructionTypeId: l.constructionTypeId,
                cost: l.cost,
            }));

            const manuallyInput = {
                contractId,
                ...otherCostType,
                calculatingType: selectedType.id,
                ...calculatingTypeValue,
                name: formData.name,
                formula,
                section,
                ...otherCode,
            };

            // + Способ создания - вручную:
            if (isManual) {
                // + запускается из 'Лимиты по прочим затратам' (Создание прочих в договоре):
                if (isUer) {
                    const variables: MutationUerCreateContractOtherCostArgs = {
                        contractId,
                        createInput: { ...manuallyInput, limits: limitsArray },
                    };

                    dispatch(createOtherManuallyRequest(variables));

                    return;
                }

                if (isCondition) {
                    const variables: BimCreateContractConditionOtherCostMutationVariables = {
                        input: { ...manuallyInput, section, chapter, otherCostLimits, formula },
                    };

                    dispatch(bimCreateContractConditionOtherCostRequest(variables));

                    return;
                }
                // - запускается из 'Лимиты по прочим затратам' (Создание прочих в договоре)

                // - запускается из НЗ:
                if (isBimWorkOrderChange) {
                    // Создание прочих Изм НЗ Бим:
                    const variables = {
                        workOrderChangeId,
                        input: { ...manuallyInput, otherCostLimits, formula, rationale: formData.rationale },
                    };

                    dispatch(bimCreateWorkOrderChangeOtherFromInputRequest(variables));

                    return;
                }

                if (workOrderStatus === WorkOrderStatusEnum.OnFormation) {
                    // Создание прочих НЗ на формировании:
                    const variables = {
                        input: { ...manuallyInput, otherCostLimits, formula },
                    };

                    dispatch(
                        bimCreateOtherToWorkOrderFromInputRequest({
                            workOrderId,
                            ...variables,
                        }),
                    );
                } else if (workOrderStatus === WorkOrderStatusEnum.InProgress) {
                    // Создание прочих НЗ на приемке:
                    const variables: BimCreateWorkOrderOtherFromInputForActMutationVariables = {
                        workOrderObjectId,
                        input: { ...manuallyInput, otherCostLimits, formula, rationale: formData.rationale },
                    };

                    dispatch(bimCreateWorkOrderOtherFromInputForActRequest(variables));
                }

                return;
                // - Запускается из НЗ
            }
            // - Способ создания - вручную

            // + Способ создания - Из справочника методик расчёта:
            const fromCatalogInput = {
                contractId,
                calculatingType: selectedType.id,
                ...calculatingTypeValue,
                otherCostLimits,
                globalOtherCostId: Number(formData?.globalOtherCostId?.id),
                formula,
                ...otherCostType,
                ...otherCode,
            };

            if (isUer) {
                const variables: MutationUerCreateContractOtherCostFromCatalogArgs = {
                    contractId,
                    createInput: { ...fromCatalogInput, limits: limitsArray },
                };

                dispatch(createContractOtherFromCatalogRequest(variables));
            } else if (isBimWorkOrderChange) {
                // Создание прочих Изм НЗ Бим:
                const variables = {
                    workOrderChangeId,
                    input: { ...fromCatalogInput, formula, rationale: formData.rationale },
                };

                dispatch(bimCreateWorkOrderChangeOtherFromCatalogRequest(variables));
            } else if (workOrderStatus === WorkOrderStatusEnum.OnFormation) {
                // Создание прочих НЗ на формировании:
                const variables = {
                    input: { ...fromCatalogInput, formula },
                };

                dispatch(bimCreateOtherToWorkOrderFromCatalogRequest({ workOrderId, ...variables }));
            } else if (workOrderStatus === WorkOrderStatusEnum.InProgress) {
                // Создание прочих НЗ на приемке:
                const variables: BimCreateWorkOrderOtherFromCatalogForActMutationVariables = {
                    workOrderObjectId,
                    input: { ...fromCatalogInput, formula, rationale: formData.rationale },
                };

                dispatch(bimCreateWorkOrderOtherFromCatalogForActRequest(variables));
            }
            // - Способ создания - Из справочника методик расчёта
        },
        [
            selectedType.id,
            selectedChapterOrSection,
            contractId,
            isManual,
            isUer,
            isBimWorkOrderChange,
            workOrderStatus,
            isCondition,
            dispatch,
            bimCreateContractConditionOtherCostRequest,
            createOtherManuallyRequest,
            workOrderChangeId,
            bimCreateWorkOrderChangeOtherFromInputRequest,
            bimCreateOtherToWorkOrderFromInputRequest,
            workOrderId,
            workOrderObjectId,
            bimCreateWorkOrderOtherFromInputForActRequest,
            createContractOtherFromCatalogRequest,
            bimCreateWorkOrderChangeOtherFromCatalogRequest,
            bimCreateOtherToWorkOrderFromCatalogRequest,
            bimCreateWorkOrderOtherFromCatalogForActRequest,
        ],
    );

    useEffect(() => {
        reset(undefined);
    }, [reset]);

    return {
        data,
        status,
        makeRequest,
        reset,
    };
};
